import { Directive, ElementRef, Renderer2, Inject, Input, HostBinding, PLATFORM_ID, NgZone } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ViewportSize } from './viewport-size';
import { isPlatformServer, DOCUMENT } from '@angular/common';
import { UserAgent } from './user-agent';
import { environment } from '@tytapp/environment';


let nextImageId = 1000;

/**
 * Implements responsive image loading for background-image
 */
@Directive({
    selector: '[responsiveBackgroundImage]',
    inputs: ['responsiveBackgroundImage']
})
export class ResponsiveBackgroundImageDirective {
    constructor(
        @Inject(DOCUMENT)
        private _doc: any,
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private viewportSize: ViewportSize,
        private domSanitizer: DomSanitizer,
        private ngZone: NgZone,
        private userAgent: UserAgent,

        @Inject(PLATFORM_ID)
        private platformId: any
    ) {
    }

    private _url: string;

    @Input()
    get responsiveBackgroundImage(): any {
        return this._url;
    }

    set responsiveBackgroundImage(url: any) {
        if (this._url === url)
            return;

        let alreadyStarted = this._url != null;
        let clientSide = typeof window !== 'undefined';

        this._url = url;

        setTimeout(() => {
            if (clientSide && this._initialized && this._visible && alreadyStarted) {
                this.fadeSwitch();
            } else {
                this.run();
            }
        });
    }

    @HostBinding(`class.responsive-background`)
    primaryClass: boolean = true;

    @HostBinding(`class.switching-background`)
    switching: boolean = false;

    private switchTimeout;

    async fadeSwitch() {
        console.log(`Fade switch`);
        let url = await this.calculateUrl(this.responsiveBackgroundImage);
        await this.preloadImage(url);

        if (isPlatformServer(this.platformId)) {
            this.switching = false;
            await this.setupImage(url);
            return;
        }

        clearTimeout(this.switchTimeout);
        this.switching = true;
        this.switchTimeout = setTimeout(async () => {
            console.log(`Set up image`);
            await this.setupImage(url);
            this.switching = false;
        }, 500);
    }

    /**
     * Whether to use the Intersection Observer API to detect when the image is nearly visible before
     * starting the load process. On server side builds, this does nothing. Instead the developer should
     * take care not to send any elements that are invisible at all.
     */
    @Input()
    whenVisible: boolean = true;

    @Input()
    widthFactor: number = 1.0;

    @Input()
    mobileWidthFactor: number = 1.0;

    private _imageId: number;

    @HostBinding(`attr.data-image-id`)
    get imageId(): any {
        if (this._imageId === undefined)
            this._imageId = nextImageId++;
        return this._imageId;
    }

    @HostBinding(`style.background-image`)
    backgroundImageStyle: any;

    _initialized: boolean = false;

    async run() {
        this._initialized = true;

        if (typeof window === 'undefined')
            await this.runForSSR();
        else
            await this.runForBrowser();
    }

    private _visible: boolean = false;

    @HostBinding('class.waiting-for-intersect')
    waitingForIntersect = false;
    /**
     * Implementation for the browser. Uses the client's full screen size and does not do
     * media query based resizing.
     */
    private async runForBrowser() {
        if (this.whenVisible && typeof IntersectionObserver !== 'undefined') {
            let element: HTMLElement = this.elementRef.nativeElement;
            let rect = element.getBoundingClientRect();
            if (rect) {

                let height = window.innerHeight;

                let verticallyAligned = (rect.top >= 0 && rect.top <= window.innerHeight) || (rect.bottom >= 0 && rect.bottom <= window.innerHeight);
                let horizontallyAligned = (rect.right >= 0 && rect.right <= window.innerWidth) || (rect.left >= 0 && rect.left <= window.innerWidth);

                if (rect.width == 0 && rect.height == 0) {
                    verticallyAligned = horizontallyAligned = false;
                }

                if (verticallyAligned && horizontallyAligned) {
                    await this.setupImage(await this.calculateUrl(this.responsiveBackgroundImage));
                    return;
                }
            }

            this.waitingForIntersect = true;

            this.ngZone.runOutsideAngular(() => {
                let observer: IntersectionObserver;
                observer = new IntersectionObserver(async (entries) => {
                    if (!entries[0] || (entries[0].intersectionRatio == 0 && !entries[0]['isIntersecting']))
                        return;

                    observer.unobserve(this.elementRef.nativeElement);
                    this.ngZone.runGuarded(() => {
                        this.waitingForIntersect = false;
                        setTimeout(async () => this.setupImage(await this.calculateUrl(this.responsiveBackgroundImage)));
                    });
                }, {
                    rootMargin: '1000px',
                    threshold: 0
                });
                observer.observe(this.elementRef.nativeElement);
            });
        } else {
            await this.setupImage(await this.calculateUrl(this.responsiveBackgroundImage));
        }
    }

    private async preloadImage(url: string) {
        if (typeof window === 'undefined')
            return;

        if (typeof url === 'object')
            return;

        console.log(`Preloading ${url}`);
        await new Promise<void>((resolve, reject) => {
            let img = document.createElement('img');
            img.src = url;
            img.style.display = 'none';
            img.addEventListener('load', () => (img.remove(), resolve()));
            document.body.appendChild(img);
        });

        console.log(`Success! Preloaded ${url}`);
    }

    /**
     * Setup the image to be shown on the background-image property.
     * If `hurry` is set, will use the XS sized image along with the
     * correctly sized image to ensure that something renders right away,
     * and then is replaced by the right one
     * @param hurry
     */
    private async setupImage(url: string) {
        this._visible = true;
        this.backgroundImageStyle = this.domSanitizer.bypassSecurityTrustStyle(`url(${url})`);
    }

    private async calculateUrl(value) {
        let widthFactor = this.widthFactor;

        // If its invalid, return a blank gif
        if (!value || (typeof value !== 'string' && !value.urls))
            return 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

        if (value.urls) {
            let assetSize = this.viewportSize.selectAssetSize(widthFactor);

            // Return the appropriate size URL, or if the asset does not
            // have the needed size, return the absolute smallest (because this
            // needs to be handled by a developer)


            return value.urls[assetSize] || value.urls['xs'];
        } else if (/\/assets\/([^\/]+\.(png|jpg))$/.test(value)) {
            let path: string = value;
            let match = path.match(/\/assets\/([^\/]+\.(png|jpg))$/);
            if (match) {
                let assetName = match[1];
                let size = this.viewportSize.selectAssetSize(this.widthFactor, this.mobileWidthFactor);
                let sizes = {
                    original: 1920,
                    xhd: 1920,
                    hd: 1920,
                    hd720: 1280,
                    sd: 1280,
                    sm: 640,
                    xs: 320
                };

                let idealWidth = sizes[size] || 1920;

                return `/assets/generated/${idealWidth}px-${assetName}`;
            } else {
                return value;
            }
        } else if (/\/\/images\.contentful/.test(value) || /images\.ctfassets/.test(value)) {
            // Contentful URL. These have some special properties!

            let url = value;
            const idealWidth = this.idealWidth();
            const supportsWebP = await this.userAgent.supportsWebP;

            if (this.widthFactor >= 1) {
                console.warn(`PERF: Image ${url} will be served at full-size (${idealWidth}px)! Is this really needed?`);
                //debugger;
            }
            url += `?w=${idealWidth}`;

            if (supportsWebP)
                url += '&fm=webp';

            return url;

        } else if (value?.includes(environment.urls.cdn)) {

            const supportsWebP = await this.userAgent.supportsWebP;
            const idealWidth = this.idealWidth();

            return [
                environment.urls.api,
                '/images/',
                value.replace(/^https?:\/\//, ''),
                '?w=',
                idealWidth,
                supportsWebP ? '&fm=webp' : ''
            ].join('');

        } else {
            return value;
        }
    }

    private idealWidth() {
        return {
            original: 1920,
            xhd: 1920,
            hd: 1920,
            hd720: 1280,
            sd: 1137,
            sm: 551,
            xs: 250
        }[this.viewportSize.selectAssetSize(this.widthFactor, this.mobileWidthFactor)] || 1920;
    }

    /**
     * Implementation for the server. Uses media queries to ensure the user agent will download the appropriate image
     * based on the viewport size.
     */
    private async runForSSR() {

        let rbi = this.responsiveBackgroundImage;
        if (!rbi)
            return;

        if (typeof rbi != 'string' && !rbi.urls)
            return;

        let rules = [];

        let sizes = {
            xs: [0, 250],
            sm: [251, 551],
            sd: [552, 1137],
            hd720: [1138, 1280],
            hd: [1281, 1920],
            xhd: [1921, /*2560*/8000],
            original: [2560, 8000],     // TODO: should be 'xxhd' but we need to populate this class of thumbnails
            xxhd: [0, 0],
        };

        let seoImageUrl = null;

        if (rbi.urls) {
            for (let size in rbi.urls) {
                let url = rbi.urls[size];
                let sizeData = sizes[size];

                if (size == 'hd') {
                    seoImageUrl = url;
                }

                let inverseWidthFactor = 1 / this.widthFactor;

                rules.push(
                    `@media (min-width: ${inverseWidthFactor * sizeData[0]}px) `
                    + `and (max-width: ${inverseWidthFactor * sizeData[1]}px) {`
                    + `[data-image-id="${this.imageId}"] {`
                    + `background-image: url(${url});`
                    + `}`
                    + `}`
                );
            }
        } else if (/\/\/images\.contentful/.test(rbi) || /images\.ctfassets\.net/.test(rbi)) {
            // Contentful URL. These have some special properties!

            const supportsWebP = await this.userAgent.supportsWebP;
            let url: string = rbi;

            const idealWidth = this.ssrIdealWidth();

            url += `?w=${idealWidth}`;

            if (supportsWebP)
                url += '&fm=webp';

            seoImageUrl = url;

            this.backgroundImageStyle = this.domSanitizer.bypassSecurityTrustStyle(`url(${url})`);
        } else if (rbi?.includes(environment.urls.cdn)) {

            const supportsWebP = await this.userAgent.supportsWebP;
            const idealWidth = this.ssrIdealWidth();

            return [
                environment.urls.api,
                '/images/',
                rbi.replace(/^https?:\/\//, ''),
                '?w=',
                idealWidth,
                supportsWebP ? '&fm=webp' : ''
            ].join('');

        } else {
            seoImageUrl = rbi;
            this.backgroundImageStyle = this.domSanitizer.bypassSecurityTrustStyle(`url(${seoImageUrl})`);
        }

        if (rules.length > 0) {
            let style = this.renderer.createElement('style');
            this.renderer.setAttribute(style, "data-image-id", `${this.imageId}`);
            this.renderer.appendChild(style,
                this.renderer.createText(
                    rules.join("\n")
                )
            );

            this.renderer.appendChild(this._doc.head, style);
        }

        if (seoImageUrl) {
            let img = this.renderer.createElement('img');
            this.renderer.setAttribute(img, 'src', seoImageUrl);
            this.renderer.setStyle(img, 'display', 'none');

            // this.renderer.setAttribute(img, 'alt') // TODO
            this.renderer.appendChild(this.elementRef.nativeElement, img);
        }
    }

    ssrIdealWidth() {
        const sizes = [250, 551, 1137, 1280, 1920];
        const assumedResolution = 1920;

        for (const size of sizes) {
            if (size >= assumedResolution * this.widthFactor) {
                return size;
            }
        }
        return assumedResolution;
    }
}
